import React from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory, {
  PaginationProvider,
  SizePerPageDropdownStandalone,
  PaginationListStandalone,
} from 'react-bootstrap-table2-paginator'
import filterFactory, { textFilter, selectFilter } from 'react-bootstrap-table2-filter'
import ToolkitProvider, { Search, CSVExport } from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min'
import { Badge } from 'reactstrap'
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css'
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css'
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css'

const { SearchBar } = Search
const { ExportCSVButton } = CSVExport

const formatDate = (d) => {
  if (!d) return '-'
  return new Date(d).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatTerm = (term) => {
  switch (term) {
    case 'P1Y': return 'Annual'
    case 'P1M': return 'Monthly'
    case 'P3Y': return '3-Year'
    default: return term || '-'
  }
}

// Days-until colour scale roughly matches the summary cards
const daysUntilFormatter = (cell) => {
  if (cell == null) return '-'
  let color = '#6c757d'
  if (cell <= 7) color = '#dc3545'
  else if (cell <= 30) color = '#ffc107'
  else if (cell <= 60) color = '#17a2b8'
  return <span style={{ color, fontWeight: 'bold' }}>{cell < 0 ? 'Past' : `${cell}d`}</span>
}

const notificationsFormatter = (cell) => {
  if (!cell || cell.length === 0) return <span className="text-muted">—</span>
  return cell.map((n) => (
    <Badge key={n} color="success" className="mr-1">{n}</Badge>
  ))
}

const CSPRenewalsTable = ({ rows }) => {
  const [filteredData, setFilteredData] = React.useState(rows)

  React.useEffect(() => { setFilteredData(rows) }, [rows])

  const columns = [
    {
      dataField: 'companyName',
      text: 'Company',
      sort: true,
      filter: textFilter({ placeholder: 'Filter...' }),
      formatter: (cell, row) => cell || row.tenantName || <span className="text-muted">Unmapped</span>,
    },
    {
      dataField: 'subscriptionName',
      text: 'Subscription',
      sort: true,
      filter: textFilter({ placeholder: 'Filter...' }),
    },
    {
      dataField: 'quantity',
      text: 'Qty',
      sort: true,
      style: { width: '70px' },
    },
    {
      dataField: 'termDuration',
      text: 'Term',
      sort: true,
      filter: selectFilter({
        options: { P1Y: 'Annual', P1M: 'Monthly', P3Y: '3-Year' },
        placeholder: 'Filter...',
      }),
      formatter: formatTerm,
      style: { width: '110px' },
    },
    {
      dataField: 'renewalDate',
      text: 'Renewal',
      sort: true,
      formatter: formatDate,
      style: { width: '120px' },
    },
    {
      dataField: 'daysUntilRenewal',
      text: 'Days Until',
      sort: true,
      formatter: daysUntilFormatter,
      style: { width: '100px' },
    },
    {
      dataField: 'status',
      text: 'Status',
      sort: true,
      // DD returns status values with unknown casing/vocabulary; use a text
      // filter rather than hardcoded select options that would silently miss.
      filter: textFilter({ placeholder: 'Filter...' }),
      formatter: (cell) => cell || '-',
      style: { width: '110px' },
    },
    {
      dataField: 'notificationsSent',
      text: 'Notified',
      formatter: notificationsFormatter,
      csvFormatter: (cell) => (cell || []).join(','),
      style: { width: '160px' },
    },
  ]

  const defaultSorted = [{ dataField: 'renewalDate', order: 'asc' }]

  return (
    <PaginationProvider
      pagination={paginationFactory({
        custom: true,
        totalSize: filteredData.length,
        sizePerPageList: [
          { text: '10', value: 10 },
          { text: '20', value: 20 },
          { text: '50', value: 50 },
          { text: '100', value: 100 },
          { text: 'All', value: filteredData.length || 1 },
        ],
      })}
    >
      {({ paginationProps, paginationTableProps }) => (
        <ToolkitProvider
          keyField="id"
          data={rows || []}
          columns={columns}
          search
          exportCSV={{
            fileName: 'CSPRenewals.csv',
            onlyExportFiltered: true,
            exportAll: true,
          }}
          onDataChange={(newData) => setFilteredData(newData)}
        >
          {(props) => (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <SearchBar
                  {...props.searchProps}
                  placeholder="Search by company, subscription, or stock code…"
                  style={{ minWidth: 360 }}
                />
                <div className="d-flex align-items-center">
                  <ExportCSVButton className="btn btn-outline-primary mr-2" {...props.csvProps}>
                    Export CSV
                  </ExportCSVButton>
                  <SizePerPageDropdownStandalone
                    btnContextual="btn-outline-primary"
                    {...paginationProps}
                  />
                </div>
              </div>

              <BootstrapTable
                bordered={false}
                hover
                condensed
                defaultSorted={defaultSorted}
                {...props.baseProps}
                pagination={paginationFactory()}
                filter={filterFactory()}
                filterPosition="top"
                {...paginationTableProps}
              />

              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted small">
                  {(() => {
                    const { page, sizePerPage } = paginationProps
                    const total = filteredData.length
                    const start = total === 0 ? 0 : (page - 1) * sizePerPage + 1
                    const end = Math.min(page * sizePerPage, total)
                    return `Showing ${start}-${end} of ${total}`
                  })()}
                </div>
                <PaginationListStandalone {...paginationProps} />
              </div>
            </div>
          )}
        </ToolkitProvider>
      )}
    </PaginationProvider>
  )
}

export default CSPRenewalsTable
